import { DEFAULT_MAX_UPLOAD_SIZE_MB } from "@/app.config"
import { $t } from "@/plugins/i18n"
import { formatDate, toDate } from "@/utils/dateUtil"

import { Role, User } from "./User"

export const accountsAnalyticsPeriodList = [
  "period7Days",
  "period30Days",
  "period90Days",
  "periodYear"
]

export const paymentSubscriptionStatuses = [
  "INCOMPLETE",
  "INCOMPLETE_EXPIRED",
  "TRIALING",
  "ACTIVE",
  "PAST_DUE",
  "CANCELED",
  "UNPAID"
]

export type PaymentSubscriptionStatus = (typeof paymentSubscriptionStatuses)[number]

export interface ShortAccount {
  id: string
  name: string
  urlName: string
  hasPaymentMethod?: boolean
}

export interface AccountPaymentSubscription {
  customerId: string
  subscriptionId: string
  status: PaymentSubscriptionStatus
  planId: string
  productId: string
  subscriptionType: string
  trialEndDate: string
  paymentMethod?: string
  paymentVerified?: boolean
  type: string
}

interface AccountProps {
  id: string
  name: string
  timesheets: boolean
  picture?: {
    pictureUrl?: string
  }
  owner?: User
  paymentSubscription?: AccountPaymentSubscription | null
  stats?: AccountStats
  createdDate?: Date
  lastActivity?: Date

  contactInformation?: AccountContactInformation | null
  urlName: string
  enabled: boolean
  test: boolean
  hasPaymentMethod?: boolean
  enterprisePermission: { [key: string]: boolean }
  hasSubscriptionAccess: boolean
  projectIdsIncludedInSubscription: { [key: string]: boolean }
  teamIdsIncludedInSubscription: { [key: string]: boolean }
  subscriptionEnabled: boolean
  settings?: AccountSettings | null
  maxUploadSizeMb?: number
}

export interface AccountSettings {
  mcpEnabled: boolean
}

/**
 * Sign-up wizard progress stored on the account by the backend (`AccountDto.setupProgress`).
 * `lastStep` is the wizard step name written by the frontend; existing accounts are migrated as completed.
 */
export interface AccountSetupProgress {
  setupCompleted: boolean
  lastStep?: string | null
}

/** Body of `PATCH /account/{id}/setup`: send the name with its URL name together, or only the owner role. */
export interface AccountSetupPayload {
  accountId: string
  name?: string
  urlName?: string
  ownerTeamRole?: Role
}

export interface AccountContactInformation {
  billingAddress: string
  unit: string
  phoneNumber: string
  contactEmail: string
}

export class AccountStats {
  projects: number
  teams: number
  users: number
  activeUsers: number

  constructor(props?) {
    this.projects = props?.projects ?? 0
    this.teams = props?.teams ?? 0
    this.users = props?.users ?? 0
    this.activeUsers = props?.activeUsers ?? 0
  }
}

export class UserCounts {
  activeUsers = 0
  deactivatedUsers = 0
  pendingInvitations = 0
  bouncedUsers = 0

  constructor(props?: Partial<UserCounts>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export interface AccountPictureUpdatePayload {
  accountId: string
  file: File
}

export interface AccountContactInformationPayload {
  accountId: string
  information: AccountContactInformation
  isSuperAdmin: boolean
}

export interface SubscriptionUpgrade {
  priceId: string
  subscriptionId: string
}

export class Account {
  id: string
  name: string
  picture: {
    pictureUrl?: string
  }
  owner?: User
  paymentSubscription?: AccountPaymentSubscription | null
  createdDate?: Date
  enabled: boolean

  stats: AccountStats
  timesheets: boolean
  contactInformation: AccountContactInformation | null
  lastActivity?: Date
  urlName: string
  test: boolean
  hasPaymentMethod: boolean
  enterprisePermission: { [key: string]: boolean }
  hasSubscriptionAccess: boolean
  projectIdsIncludedInSubscription: { [key: string]: boolean }
  teamIdsIncludedInSubscription: { [key: string]: boolean }
  subscriptionEnabled: boolean
  settings: AccountSettings
  maxUploadSizeMb: number
  constructor(props: AccountProps) {
    this.set(props)
  }

  set(props: AccountProps) {
    this.setDefaultData()

    if (!props) return

    this.id = props.id ?? ""
    this.name = props.name ?? ""
    this.picture = props.picture ?? {}
    this.owner = User.create(props.owner) ?? undefined
    this.paymentSubscription = props.paymentSubscription ?? null
    this.stats = new AccountStats(props?.stats)
    this.contactInformation = props.contactInformation ?? null
    this.createdDate = props.createdDate ?? undefined
    this.urlName = props.urlName ?? ""
    this.enabled = props.enabled ?? true
    this.subscriptionEnabled = props.subscriptionEnabled ?? true
    this.lastActivity = props.lastActivity ?? undefined
    this.test = props.test ?? false
    this.timesheets = props.timesheets ?? false
    this.hasPaymentMethod = props.hasPaymentMethod
      ? props.hasPaymentMethod
      : !!this.paymentSubscription?.paymentMethod
    this.enterprisePermission = props.enterprisePermission ?? {}
    this.hasSubscriptionAccess = props.hasSubscriptionAccess ?? false
    this.projectIdsIncludedInSubscription = props.projectIdsIncludedInSubscription ?? {}
    this.teamIdsIncludedInSubscription = props.teamIdsIncludedInSubscription ?? {}
    this.settings = { mcpEnabled: props.settings?.mcpEnabled ?? false }
    this.maxUploadSizeMb = props.maxUploadSizeMb ?? DEFAULT_MAX_UPLOAD_SIZE_MB
  }

  setDefaultData() {
    this.id = ""
    this.name = ""
    this.picture = {}
    this.owner = undefined
    this.paymentSubscription = null
    this.stats = new AccountStats()
    this.contactInformation = null
    this.createdDate = undefined
    this.urlName = ""
    this.enabled = true
    this.lastActivity = undefined
    this.test = false
    this.enterprisePermission = {}
    this.hasSubscriptionAccess = false
    this.projectIdsIncludedInSubscription = {}
    this.teamIdsIncludedInSubscription = {}
    this.subscriptionEnabled = true
    this.settings = { mcpEnabled: false }
    this.maxUploadSizeMb = DEFAULT_MAX_UPLOAD_SIZE_MB
  }

  getJsonObj() {
    return {
      id: this.id,
      name: this.name,
      ownerId: this.owner?.id ?? "",
      urlName: this.urlName
    }
  }

  static create(props: AccountProps) {
    return new Account(props)
  }

  get accountName() {
    return `${this.name} Organization`
  }

  get creationDateString() {
    if (!this.createdDate) return $t("none")
    return formatDate(this.createdDate, "LLL dd, yyyy")
  }

  get lastActivityString() {
    if (!this.lastActivity) return ""
    return formatDate(this.lastActivity, "LLL dd, yyyy")
  }
}

export class DemoAccount {
  accountCreated = false
  deleted = false
  email = ""
  fullName = ""
  id = ""
  message = ""
  phoneNumber = ""
  createdDate = new Date()

  constructor(props?: Partial<DemoAccount>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.createdDate = toDate(this.createdDate)
  }

  get parseRequestDate() {
    return formatDate(this.createdDate, "hh:mm a MM/dd/yy")
  }
}

export class AccountsAnalyticsByPeriod {
  accountsCreated = {
    last: 0,
    prev: 0
  }
  demoRequests = {
    last: 0,
    prev: 0
  }

  constructor(props?: Partial<AccountsAnalyticsByPeriod>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class AccountsAnalyticsActive {
  totalActive = 0
  activeLast30Days = 0

  constructor(props?: Partial<AccountsAnalyticsActive>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class AccountsAnalyticsUsers {
  totalActiveUsersInActiveAccounts = 0
  activeLast30DaysInActiveAccounts = 0
  pendingInvites = 0

  constructor(props?: Partial<AccountsAnalyticsUsers>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class AccountsAnalytics {
  period7Days: AccountsAnalyticsByPeriod = new AccountsAnalyticsByPeriod()
  period30Days: AccountsAnalyticsByPeriod = new AccountsAnalyticsByPeriod()
  period90Days: AccountsAnalyticsByPeriod = new AccountsAnalyticsByPeriod()
  periodYear: AccountsAnalyticsByPeriod = new AccountsAnalyticsByPeriod()
  accounts: AccountsAnalyticsActive = new AccountsAnalyticsActive()
  users: AccountsAnalyticsUsers = new AccountsAnalyticsUsers()

  constructor(props?: Partial<AccountsAnalytics>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}
